/**
 * AuthProvider — Weiche zwischen den beiden Auth-Modi (VITE_AUTH_MODE).
 *
 *  - "dev": kein Logto-Code wird geladen (lazy import), die Dev-Identitaet
 *    (admin) steht sofort im Context — Kinder direkt rendern.
 *  - "authentik": laedt AuthentikAuth (oidc-client-ts gegen auth.rmnc.app).
 *  - "logto": laedt LogtoAuth (LogtoProvider + Login-Gate + /callback) nach,
 *    echter OIDC-Login gegen die Kunden-Logto-Instanz (user.rmnc.app).
 *
 * Muss INNERHALB des BrowserRouter haengen (LogtoAuth nutzt useLocation/useNavigate).
 *
 * UI-Rollen-Gates (docs/PERMISSIONS.md, app:technik:*) nutzen useAuth().user.roles
 * bzw. die Helfer useHasRole/useHasAnyRole unten — das ist reines UI-Gating
 * (z. B. "lesen" sieht keine Bearbeiten-Buttons). Die serverseitige Pruefung
 * (require_permission_set in backend/app/core/security.py) existiert unabhaengig
 * davon bereits und bleibt die einzige durchsetzende Instanz.
 */

import { createContext, lazy, Suspense, useContext, type ReactNode } from "react"
import { Navigate } from "react-router-dom"
import { AUTH_MODE } from "./store"
import { Button } from "@/components/ui"

const LogtoAuth = lazy(() => import("./LogtoAuth"))
const AuthentikAuth = lazy(() => import("./AuthentikAuth"))

export interface AuthUser {
  sub: string
  name: string
  roles: string[]
}

export interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  signIn: () => void
  signOut: () => void
}

const DEV_USER: AuthUser = {
  sub: "dev-admin",
  name: "Dev Admin",
  roles: ["admin"],
}

const DEV_CONTEXT_VALUE: AuthContextValue = {
  user: DEV_USER,
  isAuthenticated: true,
  signIn: () => {},
  signOut: () => {},
}

export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isAuthenticated: false,
  signIn: () => {},
  signOut: () => {},
})

/** Vollflaechige Ladeanzeige waehrend Login/Redirect (deutsch, ohne Layout) */
export function AuthLoading({ text = "Anmeldung wird geprüft…" }: { text?: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3 text-slate-500">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-900 border-t-transparent" />
        <span className="text-sm">{text}</span>
      </div>
    </div>
  )
}

/** Fehlende Build-Konfiguration klar benennen statt kryptisch fehlzuschlagen */
export function ConfigError({ fehlend }: { fehlend: string[] }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md space-y-2 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Anmeldung nicht konfiguriert</h1>
        <p className="text-sm text-slate-600">
          Der Build wurde ohne vollständige Anmelde-Konfiguration erstellt. Es fehlen:{" "}
          <code>{fehlend.join(", ")}</code>. Bitte an die Administration wenden.
        </p>
      </div>
    </div>
  )
}

/** Verstaendliche deutsche Fehlerseite (z. B. abgebrochener Login) */
export function AuthError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md space-y-4 text-center">
        <h1 className="text-lg font-semibold text-slate-900">Anmeldung fehlgeschlagen</h1>
        <p className="text-sm text-slate-600">
          Die Anmeldung wurde abgebrochen oder konnte nicht abgeschlossen werden. Bitte
          versuchen Sie es erneut — bei wiederholten Problemen an die Administration wenden.
        </p>
        <Button onClick={onRetry}>Erneut anmelden</Button>
      </div>
    </div>
  )
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "dev") {
    return <AuthContext.Provider value={DEV_CONTEXT_VALUE}>{children}</AuthContext.Provider>
  }
  if (AUTH_MODE === "authentik") {
    return (
      <Suspense fallback={<AuthLoading />}>
        <AuthentikAuth>{children}</AuthentikAuth>
      </Suspense>
    )
  }
  return (
    <Suspense fallback={<AuthLoading />}>
      <LogtoAuth>{children}</LogtoAuth>
    </Suspense>
  )
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

/** UI-Gating: hat der angemeldete User eine bestimmte Rolle (app:technik:*)? */
export function useHasRole(role: string): boolean {
  const { user } = useAuth()
  return user?.roles.includes(role) ?? false
}

/** UI-Gating: hat der angemeldete User mindestens eine der angegebenen Rollen? */
export function useHasAnyRole(roles: readonly string[]): boolean {
  const { user } = useAuth()
  if (!user) return false
  return roles.some((r) => user.roles.includes(r))
}
